import { getRestaurant } from "@aitisal/sdk/restaurant";
import { getRole } from "@aitisal/sdk/user/role";
import { Center, Loader } from "@mantine/core";
import { useRouter } from "@tanstack/react-router";
import { fetchAuthSession, fetchUserAttributes } from "aws-amplify/auth";
import { useEffect, useState } from "react";

import { Restaurant } from "@aitisal/contracts/restaurant";
import { useQuery } from "@tanstack/react-query";
import { initializePushNotifications } from "../../helper/pushNotifications";
import { useAuthStatus } from "../../hooks/useAuthStatus";
import {
  createAbilities,
  useAbilityStore,
} from "../../stores/rolesRightsStore";
import { useRestaurant } from "../../stores/stores";

// Removed global hasInitialized flag to allow proper re-initialization on server restart

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuthStatus();
  const {
    setRestaurantId,
    setBrandId,
    setUserId,
    setRestaurant,
    setUserRole,
    restaurantId,
  } = useRestaurant();

  const { data: restaurantData } = useQuery<Restaurant>({
    queryKey: ["restaurant", restaurantId],
    queryFn: async () => {
      const id = restaurantId;
      if (!id) throw new Error("No restaurant ID available");
      const response = await getRestaurant(id);
      return response.data;
    },
    enabled: !!restaurantId,
  });

  useEffect(() => {
    if (restaurantData) {
      setRestaurant(restaurantData);
    }
  }, [restaurantId, restaurantData, setRestaurant]);

  const { setAbility } = useAbilityStore();
  const [initializing, setInitializing] = useState(true);

  const navigate = useRouter();

  // Initialize app data when authenticated
  useEffect(() => {
    let isMounted = true;

    // Helper to check if an error is a network/timeout error vs an actual auth error
    const isNetworkError = (err: unknown): boolean => {
      if (err instanceof Error) {
        const message = err.message.toLowerCase();
        const name = err.name.toLowerCase();
        return (
          message.includes("network") ||
          message.includes("timeout") ||
          message.includes("fetch") ||
          message.includes("econnrefused") ||
          message.includes("enotfound") ||
          message.includes("failed to fetch") ||
          name.includes("networkerror") ||
          name.includes("timeouterror") ||
          name === "typeerror"
        );
      }
      return false;
    };

    const initAppData = async () => {
      // Wait for auth status to be determined
      if (authLoading) return;

      // If not authenticated, redirect to login
      if (!isAuthenticated) {
        navigate.navigate({ to: "/login" });
        setInitializing(false);
        return;
      }

      try {
        // Fetch user details from token
        let signInDetails: any = await fetchUserAttributes().catch(
          async (err) => {
            console.warn(
              "Could not fetch fresh user attributes, attempting to use cached token:",
              err,
            );
            try {
              const session = await fetchAuthSession();
              return session.tokens?.idToken?.payload;
            } catch (tokenErr) {
              console.error("Could not retrieve token payload:", tokenErr);
              return null;
            }
          },
        );

        if (!signInDetails) {
          console.error("No sign in details available");
          if (isMounted) setInitializing(false);
          return;
        }

        const fetchedRestaurantId = restaurantId;
        const brandId = signInDetails["custom:brandId"];
        const userId = signInDetails["custom:userId"];
        const roleId = signInDetails["custom:roleId"];

        if (!fetchedRestaurantId) {
          if (isMounted) setInitializing(false);
          return;
        }

        // Set restaurant ID from Zustand persisted state (already handled by store)
        if (!restaurantId && fetchedRestaurantId) {
          setRestaurantId(fetchedRestaurantId);
        }
        setBrandId(brandId || "");
        setUserId(userId || "");

        // Setup abilities
        if (roleId) {
          try {
            const role = await getRole(roleId);
            const permissions = role?.data?.permissions;
            const newAbilities = createAbilities(permissions);
            setAbility(newAbilities);
            setUserRole(role?.data);
          } catch (roleError) {
            console.error("Error fetching user role:", roleError);
            if (isMounted) setInitializing(false);
            return;
          }
        }

        // Setup push notifications
        try {
          if (userId) {
            await initializePushNotifications(userId, (notification) => {
              console.log("Notification tapped:", notification);
            });
          }
        } catch (pushErr) {
          console.warn("Push notification setup skipped:", pushErr);
        }
      } catch (error) {
        console.error("Error during initialization:", error);
        if (isNetworkError(error)) {
          console.warn(
            "Network error during initialization, keeping current auth state",
          );
        }
      } finally {
        if (isMounted) {
          setInitializing(false);
        }
      }
    };

    initAppData();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, authLoading]);

  if (initializing || authLoading) {
    return (
      <Center className="min-h-screen">
        <Loader size="lg" color="black" />
      </Center>
    );
  }

  return (
    <>
      <audio
        style={{ display: "none" }}
        id="beep-sound"
        src="/aitisal_order_notification.mp3"
        preload="auto"
      ></audio>
      {children}
    </>
  );
}
